import { useState, useEffect } from 'react';
import api, { downloadCsv } from '../utils/api';
import { useUrlParams } from '../hooks/useUrlState';
import StateBlock from '../components/StateBlock';
import LedgerTable from '../components/LedgerTable';

const ENTITY_TYPES = [
  { key: 'client', label: 'Client', endpoint: 'clients', mode: 'ar' },
  { key: 'vendor', label: 'Vendor', endpoint: 'vendors', mode: 'ap' },
  { key: 'contractor', label: 'Contractor', endpoint: 'contractors', mode: null }, // special-cased: 2 ledgers
];

export default function LedgersPage() {
  const { get, set } = useUrlParams();
  const entityType = get('type', 'client');
  const [entities, setEntities] = useState([]);
  const selectedId = get('id', '');
  const search = get('search', '');

  useEffect(() => {
    const config = ENTITY_TYPES.find(t => t.key === entityType);
    api.get(`/${config.endpoint}`).then(r => setEntities(r.data));
  }, [entityType]);

  const filteredEntities = entities.filter(e => {
    const name = e.name || `${e.first_name} ${e.last_name}`;
    return name.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Ledgers</h1>
          <p className="page-subtitle">Every invoice and payment for a specific Client, Vendor, or Contractor, with a running balance.</p>
        </div>
      </div>

      <div className="filter-bar">
        <select value={entityType} onChange={e => set({ type: e.target.value, id: '' })}>
          {ENTITY_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <input
          type="text"
          className="filter-search"
          placeholder={`Search ${entityType}s by name…`}
          value={search}
          onChange={e => set({ search: e.target.value })}
        />
        <select value={selectedId} onChange={e => set({ id: e.target.value })} style={{ minWidth: 240 }}>
          <option value="">Select a {entityType}…</option>
          {filteredEntities.map(e => (
            <option key={e.id} value={e.id}>{e.name || `${e.first_name} ${e.last_name}`}</option>
          ))}
        </select>
      </div>

      {!selectedId && (
        <StateBlock type="empty" title="Pick an entity above" message="Select a Client, Vendor, or Contractor to see their full transaction ledger." />
      )}

      {selectedId && entityType !== 'contractor' && (
        <SingleLedgerView entityType={entityType} entityId={selectedId} />
      )}

      {selectedId && entityType === 'contractor' && (
        <ContractorLedgerView contractorId={selectedId} />
      )}
    </div>
  );
}

function SingleLedgerView({ entityType, entityId }) {
  const config = ENTITY_TYPES.find(t => t.key === entityType);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setData(null);
    setError(false);
    api.get(`/${config.endpoint}/${entityId}/ledger`).then(r => setData(r.data)).catch(() => setError(true));
  }, [config, entityId]);

  async function handleExport() {
    await downloadCsv(`/${config.endpoint}/${entityId}/ledger/export`, `${entityType}-ledger.csv`);
  }

  if (error) return <StateBlock type="error" />;
  if (!data) return <StateBlock type="loading" />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button className="btn btn-secondary" onClick={handleExport}>↓ Export CSV</button>
      </div>
      <LedgerTable mode={config.mode} rows={data.rows} summary={data.summary} />
    </div>
  );
}

function ContractorLedgerView({ contractorId }) {
  const { get, set } = useUrlParams();
  const tab = get('tab', 'ap');
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setData(null);
    setError(false);
    api.get(`/contractors/${contractorId}/ledger/${tab}`).then(r => setData(r.data)).catch(() => setError(true));
  }, [contractorId, tab]);

  async function handleExport() {
    await downloadCsv(`/contractors/${contractorId}/ledger/${tab}/export`, `contractor-${tab}-ledger.csv`);
  }

  return (
    <div>
      <div className="panel-tabs" style={{ padding: 0, marginBottom: 16 }}>
        <button className={'panel-tab' + (tab === 'ap' ? ' active' : '')} onClick={() => set({ tab: 'ap' })}>AP Ledger</button>
        <button className={'panel-tab' + (tab === 'ar' ? ' active' : '')} onClick={() => set({ tab: 'ar' })}>AR Ledger</button>
        <button className={'panel-tab' + (tab === 'combined' ? ' active' : '')} onClick={() => set({ tab: 'combined' })}>Combined</button>
      </div>

      {error && <StateBlock type="error" />}
      {!error && !data && <StateBlock type="loading" />}

      {!error && data && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button className="btn btn-secondary" onClick={handleExport}>↓ Export CSV</button>
          </div>
          <LedgerTable
            mode={tab}
            rows={data.rows}
            summary={tab !== 'combined' ? data.summary : undefined}
            apSummary={tab === 'combined' ? data.ap_summary : undefined}
            arSummary={tab === 'combined' ? data.ar_summary : undefined}
          />
        </div>
      )}
    </div>
  );
}
